import { mergeAttributes, Node, type Editor as TEditor } from "@tiptap/core";
import { NodeViewWrapper, ReactNodeViewRenderer, type ReactNodeViewProps } from "@tiptap/react";
import { ExternalLink, Link2, MoreHorizontal, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { apiFetch } from "../../api";
import { Menu, useAnchor } from "../Menu";

/*
 * Evernote's rich link card ("--en-richlink"): a URL shown as a card with the page's title,
 * description, image and site. The fields live on the node as data-* attributes, so the card renders
 * offline and the HTML stays readable (a plain link inside). Cards without fields (imported from
 * Evernote, which doesn't export them) are filled for display from /api/unfurl, without editing the note.
 */

export interface LinkMeta {
  url: string;
  title: string;
  description: string;
  image: string;
  site: string;
}

const memo = new Map<string, Promise<LinkMeta | null>>();
const STORE = "engram.unfurl";

/** Card fields for a URL: in-memory, then this device's cache, then the server. */
export function fetchLinkMeta(url: string): Promise<LinkMeta | null> {
  let p = memo.get(url);
  if (p) return p;
  p = (async () => {
    try {
      const cached = JSON.parse(localStorage.getItem(STORE) ?? "{}") as Record<string, LinkMeta>;
      if (cached[url]) return cached[url]!;
    } catch {
      /* no storage */
    }
    try {
      const r = await apiFetch(`/unfurl?url=${encodeURIComponent(url)}`);
      if (!r.ok) return null;
      const meta = (await r.json()) as LinkMeta;
      try {
        const all = JSON.parse(localStorage.getItem(STORE) ?? "{}") as Record<string, LinkMeta>;
        const keys = Object.keys(all);
        if (keys.length > 200) for (const k of keys.slice(0, keys.length - 200)) delete all[k];
        all[url] = meta;
        localStorage.setItem(STORE, JSON.stringify(all));
      } catch {
        /* no storage */
      }
      return meta;
    } catch {
      memo.delete(url); // offline: try again next time
      return null;
    }
  })();
  memo.set(url, p);
  return p;
}

export const isWebUrl = (s: string) => /^https?:\/\/[^\s<>"]+$/i.test(s.trim());

const field = (name: string) => ({
  default: "",
  parseHTML: (el: HTMLElement) => el.getAttribute(`data-${name}`) ?? "",
  renderHTML: (a: Record<string, string>) => (a[name] ? { [`data-${name}`]: a[name] } : {}),
});

export const RichLink = Node.create({
  name: "richLink",
  group: "block",
  atom: true,
  draggable: true,
  addAttributes() {
    return {
      url: {
        default: "",
        parseHTML: (el) => {
          const u = el.getAttribute("data-url") || el.querySelector("a")?.getAttribute("href") || "";
          return isWebUrl(u) ? u : "";
        },
        renderHTML: (a) => ({ "data-url": a.url }),
      },
      title: field("title"),
      description: field("description"),
      image: { ...field("image"), parseHTML: (el: HTMLElement) => (/^https:\/\//i.test(el.getAttribute("data-image") ?? "") ? el.getAttribute("data-image")! : "") },
      site: field("site"),
    };
  },
  parseHTML() {
    return [{ tag: "div[data-rich-link]" }];
  },
  renderHTML({ node, HTMLAttributes }) {
    // A plain link inside keeps the note readable without the app (exports, backup).
    return ["div", mergeAttributes({ "data-rich-link": "" }, HTMLAttributes), ["a", { href: node.attrs.url }, node.attrs.title || node.attrs.url]];
  },
  renderText({ node }) {
    return [node.attrs.title, node.attrs.url].filter(Boolean).join(" ");
  },
  addNodeView() {
    return ReactNodeViewRenderer(RichLinkView);
  },
});

function RichLinkView({ node, editor, getPos, deleteNode, selected }: ReactNodeViewProps) {
  const a = node.attrs as LinkMeta;
  const [meta, setMeta] = useState<LinkMeta | null>(a.title ? a : null);
  const menu = useAnchor();
  useEffect(() => {
    if (a.title) return setMeta(a);
    let live = true;
    void fetchLinkMeta(a.url).then((m) => live && m && setMeta(m));
    return () => {
      live = false;
    };
  }, [a.url, a.title]); // eslint-disable-line react-hooks/exhaustive-deps
  let host = a.url;
  try {
    host = new URL(a.url).hostname.replace(/^www\./, "");
  } catch {
    /* keep raw */
  }
  const m = meta ?? a;
  const open = () => window.open(a.url, "_blank", "noopener,noreferrer");
  return (
    <NodeViewWrapper className={`rich-link${selected ? " selected" : ""}`} data-drag-handle="">
      <a
        className="rich-link-card"
        href={a.url}
        contentEditable={false}
        onClick={(e) => {
          e.preventDefault();
          // Like a link: a click opens it when reading; when editing, Ctrl/Cmd-click or the menu does.
          if (!editor.isEditable || e.ctrlKey || e.metaKey) open();
        }}
      >
        {m.image && <img className="rich-link-img" src={m.image} alt="" loading="lazy" referrerPolicy="no-referrer" onError={(e) => (e.currentTarget.style.display = "none")} />}
        <span className="rich-link-text">
          <span className="rich-link-title">{m.title || a.url}</span>
          {m.description && <span className="rich-link-desc">{m.description}</span>}
          <span className="rich-link-site">{m.site || host}</span>
        </span>
      </a>
      {editor.isEditable && (
        <button className="icon-btn rich-link-more" contentEditable={false} title="Link options" onMouseDown={(e) => e.preventDefault()} onClick={menu.open}>
          <MoreHorizontal size={16} />
        </button>
      )}
      {menu.anchor && (
        <Menu
          anchor={menu.anchor}
          align="end"
          onClose={menu.close}
          entries={[
            { label: "Open link", icon: <ExternalLink size={14} />, onSelect: open },
            { label: "Show as link", icon: <Link2 size={14} />, onSelect: () => cardToLink(editor, getPos() ?? -1, node.attrs as LinkMeta) },
            { separator: true },
            { label: "Remove", icon: <Trash2 size={14} />, danger: true, onSelect: deleteNode },
          ]}
        />
      )}
    </NodeViewWrapper>
  );
}

function cardToLink(editor: TEditor, pos: number, a: LinkMeta) {
  if (pos < 0) return;
  const node = editor.state.doc.nodeAt(pos);
  if (!node) return;
  editor
    .chain()
    .focus()
    .insertContentAt({ from: pos, to: pos + node.nodeSize }, { type: "paragraph", content: [{ type: "text", text: a.url, marks: [{ type: "link", attrs: { href: a.url } }] }] })
    .run();
}

/**
 * Turns the link at [from, to] into a card. A paragraph holding only that link becomes the card;
 * otherwise the link text goes and the card lands after its paragraph.
 */
export async function linkToCard(editor: TEditor, from: number, to: number, url: string) {
  const meta = (await fetchLinkMeta(url)) ?? { url, title: "", description: "", image: "", site: "" };
  if (editor.isDestroyed) return;
  const { doc } = editor.state;
  const $from = doc.resolve(from);
  const para = $from.parent;
  const card = { type: "richLink", attrs: { ...meta, url } };
  if (para.isTextblock && para.textContent.trim() === doc.textBetween(from, to).trim() && $from.depth >= 1) {
    const start = $from.before();
    editor.chain().focus().insertContentAt({ from: start, to: start + para.nodeSize }, card).run();
  } else {
    const after = $from.after();
    editor.chain().focus().insertContentAt(after, card).deleteRange({ from, to }).run();
  }
}

/** The link under the caret, as a range (for "Show as card" on an existing link). */
export function linkAtCaret(editor: TEditor): { from: number; to: number; url: string } | null {
  const { $from } = editor.state.selection;
  const type = editor.schema.marks.link;
  if (!type) return null;
  const parent = $from.parent;
  let start = -1;
  let end = -1;
  let href = "";
  parent.forEach((child, offset) => {
    const mark = child.marks.find((mk) => mk.type === type);
    const from = $from.start() + offset;
    const to = from + child.nodeSize;
    if (mark && from <= $from.pos && $from.pos <= to && start < 0) {
      start = from;
      end = to;
      href = mark.attrs.href as string;
    } else if (mark && start >= 0 && from === end && mark.attrs.href === href) end = to;
  });
  return start >= 0 && isWebUrl(href) ? { from: start, to: end, url: href } : null;
}
